import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Navbar } from 'react-bootstrap';
import DOMPurify from 'dompurify';

function App() {
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    email: '',
    mobile: '+34 6xx xxx xxx',
    company: 'APM',
    street: 'Calle Alicante, 27',
    city: 'Santa Ponsa',
    zip: '07180',
    phone: '+34 971 698 900',
    banner: 'https://e-signature.apm-mallorca.com/banner/banner.gif',
    web: 'https://www.apm-mallorca.com',
    webshow: 'https://www.apm-mallorca.com',
  });

  const [signatureHtml, setSignatureHtml] = useState('');
  const [template, setTemplate] = useState('');

  useEffect(() => {
    fetch('/apm-template.txt') // Changed to .txt to avoid 'serve -s' redirecting .html to index.html
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(data => {
        if (data.includes('<!DOCTYPE html>') && data.includes('<div id="root">')) {
          console.error('Loaded index.html instead of template. Check server routing.');
          return;
        }
        setTemplate(data);
      })
      .catch(error => console.error('Error loading template:', error));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const generateSignature = () => {
    let generatedLayout = template;
    for (const [key, value] of Object.entries(formData)) {
      const placeholder = `[[${key.toUpperCase()}]]`;
      const sanitizedValue = DOMPurify.sanitize(value);
      generatedLayout = generatedLayout.replace(new RegExp(escapeRegExp(placeholder), 'g'), sanitizedValue);
    }
    generatedLayout = generatedLayout.replace(/[[A-Z_]+]]/g, '');
    setSignatureHtml(generatedLayout);
  };

  const downloadSignature = () => {
    const blob = new Blob([signatureHtml], { type: 'text/html' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'signature.html';
    link.click();
  };

  const copyToClipboard = () => {
    if (signatureHtml) {
      const blob = new Blob([signatureHtml], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      navigator.clipboard.write([clipboardItem]).then(() => {
        alert('Signature copied to clipboard!');
      }, (err) => {
        console.error('Could not copy text: ', err);
        alert('Failed to copy signature.');
      });
    }
  };

  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  return (
    <div className="pinnacle-wrapper">
      <Navbar bg="light" expand="lg">
        <Container>
          <Navbar.Brand href="#">
            <img alt="Logotipo Engel & Voelkers" src="https://www.apm-mallorca.com/wp-content/uploads/icon.svg" height="45px" />
          </Navbar.Brand>
        </Container>
      </Navbar>

      <Container className="mt-4">
        <div className="page-header">
          <h1 className="main-header">APM E-Mail signature generator</h1>
        </div>

        <Form>
          <Row>
            <Col md={6}>
              <h2>Personal Information</h2>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control type="text" name="name" placeholder="Enter your name" onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Position in Company</Form.Label>
                <Form.Control type="text" name="position" placeholder="Enter your Position" onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" name="email" placeholder="Enter your email" onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Mobile Phone Number</Form.Label>
                <Form.Control type="phone" name="mobile" value={formData.mobile} onChange={handleChange} />
              </Form.Group>
            </Col>

            <Col md={6}>
              <h2>Company Information</h2>
              <Form.Group className="mb-3">
                <Form.Label>Current BANNER</Form.Label>
                <img alt="Current Banner" src={formData.banner} height="150px" className="d-block" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Company BANNER URL</Form.Label>
                <Form.Control type="text" name="banner" value={formData.banner} disabled />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Website URL/LINK</Form.Label>
                <Form.Control type="text" name="web" value={formData.web} disabled />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Website Display Name</Form.Label>
                <Form.Control type="text" name="webshow" value={formData.webshow} disabled />
              </Form.Group>
            </Col>
          </Row>

          <hr />

          <Row>
            <Col md={6}>
              <h2>Address Information</h2>
              <Form.Group className="mb-3">
                <Form.Label>Company</Form.Label>
                <Form.Control type="text" name="company" value={formData.company} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Street Address</Form.Label>
                <Form.Control type="text" name="street" value={formData.street} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>City</Form.Label>
                <Form.Control type="text" name="city" value={formData.city} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Zip Code</Form.Label>
                <Form.Control type="text" name="zip" value={formData.zip} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control type="phone" name="phone" value={formData.phone} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>

          <Button variant="primary" onClick={generateSignature} className="me-2">Preview</Button>
          <Button variant="secondary" onClick={downloadSignature} className="me-2" disabled={!signatureHtml}>Download</Button>
          <Button variant="info" onClick={copyToClipboard} disabled={!signatureHtml}>Copy to clipboard</Button>
        </Form>

        <h3 className="main-header mt-4">Copy & Paste</h3>
        <iframe srcDoc={signatureHtml} title="preview" width="100%" height="500" style={{ border: '1px solid #ccc' }} />
      </Container>
    </div>
  );
}

export default App;
